import {
    getFirestore,
    collection,
    query,
    onSnapshot,
    DocumentData,
    QuerySnapshot,
    doc,
    getDoc,
    Firestore,
    Timestamp,
    QueryFieldFilterConstraint,
    updateDoc
} from "firebase/firestore";
import { FirebaseConfig } from "./firebaseConfig";
import AuthenticationService, { useAuthService } from "../modules/login/authentication";
import { useState, useEffect } from "react";
import { User } from "firebase/auth";
import { FirebaseStorage, getStorage } from "firebase/storage";
import { Functions, getFunctions } from "firebase/functions";

export interface MessageDocument {
    id: string;
    title: string;
    body: string;
    duration: number;
    createdDate: Timestamp;
    updatedDate?: Timestamp;
    audience: string[];
    notificationType: string;
    sentTo: { total: number, delivered: number, read: number, ignored: number };
}

export interface OrganizationDocument {
    id: string;
    name: string;
    logoUrl?: string;
    registrationKey: string;
    tier: string;
    expiryDate: Timestamp;
    isSelfSignupAllowed: boolean;
}

export interface Person {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    domainName: string;
    username: string;
    externalId: string;
    groups: string[];
}

export const useFirebaseService = () => {
    const authService = useAuthService();
    const [firebaseService, setFirebaseService] = useState<FirebaseService | null>(null);

    const handleUserChange = (user: User | null) => {
        if (authService.getOrgId()) {
            setFirebaseService(new FirebaseService(authService));
        }
    };


    useEffect(() => {
        if (authService) {
            if (authService.getOrgId()) {
                setFirebaseService(new FirebaseService(authService));
            }
        }
    }, [authService]);

    if (authService) {
        authService.onUserChange(handleUserChange);
    }

    return firebaseService;
};

export class FirebaseService {
    public db: Firestore;
    public storage: FirebaseStorage;
    private authService: AuthenticationService;
    public functions: Functions

    constructor(authService: AuthenticationService) {
        this.authService = authService;
        this.db = getFirestore(FirebaseConfig.app);
        this.storage = getStorage(FirebaseConfig.app);
        this.functions = getFunctions(FirebaseConfig.app);
    }

    public getCollection(path: string) {
        return collection(this.db, `organizations/${this.authService.getOrgId()}/${path}`)
    }
    // Fetch documents from Firestore
    // async fetchDocuments(collectionPath: string): Promise<FirestoreDocument[]> {
    //     const collectionRef = collection(this.db, collectionPath);
    //     const q = query(collectionRef, 
    //         where('isResolved', '!=', true), 
    //    //     orderBy('createdDate', 'asc'),
    //    );
    //     const querySnapshot = await getDocs(q);

    //     const documents: FirestoreDocument[] = [];
    //     querySnapshot.forEach((doc) => {
    //         documents.push(doc.data() as FirestoreDocument);
    //     });

    //     return documents;
    // }

    // Subscribe to Firestore document changes
    async subscribeToDocuments(
        collectionPath: string,
        callback: (snapshot: QuerySnapshot<DocumentData>

        ) => void,
        filter: QueryFieldFilterConstraint | undefined = undefined,
    ) {
        const fullPath = `organizations/${this.authService.getOrgId()}/${collectionPath}`;
        const collectionRef = collection(this.db, fullPath);
        const q = filter ? query(collectionRef,
            filter,
            //    orderBy('createdDate', 'desc'),
        ) : query(collectionRef);
        onSnapshot(q, callback);
    }

    async getDocument<TDocumentType>(collectionPath: string, documentId: string): Promise<TDocumentType | null> {
        const fullPath = `organizations/${this.authService.getOrgId()}/${collectionPath}`;
        const docRef = doc(this.db, fullPath, documentId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() } as TDocumentType;
        } else {
            return null;
        }
    }

    async getOrganizationDocument(): Promise<OrganizationDocument> {
        const docRef = doc(this.db, "organizations", this.authService.getOrgId());

        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() } as OrganizationDocument;
        } else {
            console.log('No such document!');
            throw new Error('Organization document not found');
        }
    }

    async updateOrganizationDocument(data: any) {
        const orgRef = doc(this.db, 'organizations', this.authService.getOrgId());
        await updateDoc(orgRef, data);
    }

}
